import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';
import { request, fmtDate, fmtNum, pointUrl } from '@/lib/api';
import { Pager, PAGE_SIZE } from '@/components/Pager';
import { RefreshButton } from '@/components/RefreshButton';

const STATUSES = ['queued', 'running', 'done', 'error'] as const;

const STATUS_STYLE: Record<string, string> = {
  queued: 'bg-sky-100 text-sky-800',
  running: 'bg-amber-100 text-amber-800',
  done: 'bg-emerald-100 text-emerald-800',
  error: 'bg-red-100 text-red-800',
};

interface ResearchRow {
  id: string;
  status: (typeof STATUSES)[number];
  error: string | null;
  created_at: string;
  updated_at: string;
  point_id: string;
  point_title: string | null;
  point_category: string;
  share_code: string | null;
  user_email: string;
}

export function ResearchPage() {
  const { t } = useTranslation();
  const [status, setStatus] = useState('');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<{ rows: ResearchRow[]; total: number } | null>(null);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
    if (status) params.set('status', status);
    request<{ rows: ResearchRow[]; total: number }>(`/backoffice/research?${params}`)
      .then(setData)
      .catch(() => setError(true));
  }, [status, offset, reloadKey]);

  const refresh = () => setReloadKey((k) => k + 1);

  if (error) return <p className="text-sm text-red-600">{t('common.error')}</p>;

  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold">
          {t('research.title')}
          {data && <span className="ml-2 text-base font-normal text-stone-400">{fmtNum(data.total)}</span>}
        </h1>
        <RefreshButton onClick={refresh} />
      </div>
      <p className="mt-1 text-sm text-stone-500">{t('research.subtitle')}</p>

      <select
        value={status}
        onChange={(e) => {
          setStatus(e.target.value);
          setOffset(0);
        }}
        className="mt-4 rounded-md border border-stone-300 bg-white px-3 py-2 text-sm focus:border-amber-600 focus:outline-none"
      >
        <option value="">{t('research.allStatuses')}</option>
        {STATUSES.map((s) => (
          <option key={s} value={s}>
            {t(`research.statuses.${s}`)}
          </option>
        ))}
      </select>

      {!data ? (
        <p className="mt-6 text-sm text-stone-500">{t('common.loading')}</p>
      ) : data.rows.length === 0 ? (
        <p className="mt-6 text-sm text-stone-500">{t('common.empty')}</p>
      ) : (
        <>
          <div className="mt-4 overflow-x-auto rounded-xl bg-white shadow-sm">
            <table className="w-full min-w-[680px] text-left text-sm">
              <thead className="border-b border-stone-200 text-xs uppercase text-stone-500">
                <tr>
                  <th className="px-4 py-3">{t('research.point')}</th>
                  <th className="px-4 py-3">{t('research.requestedBy')}</th>
                  <th className="px-4 py-3">{t('research.status')}</th>
                  <th className="px-4 py-3">{t('research.requested')}</th>
                  <th className="px-4 py-3">{t('research.updated')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {data.rows.map((r) => {
                  const url = pointUrl(r.share_code);
                  const title = r.point_title || t('points.noTitle');
                  return (
                    <tr key={r.id} className="hover:bg-stone-50">
                      <td className="max-w-[280px] px-4 py-3">
                        <span className="flex items-center gap-1.5">
                          <span className="truncate font-medium">{title}</span>
                          {url && (
                            <a
                              href={url}
                              target="_blank"
                              rel="noreferrer"
                              title={t('common.openInApp')}
                              className="shrink-0 text-stone-400 hover:text-amber-700"
                            >
                              <ExternalLink size={14} />
                            </a>
                          )}
                        </span>
                        <span className="text-xs text-stone-400">
                          {t(`categories.${r.point_category}`, r.point_category)}
                        </span>
                      </td>
                      <td className="max-w-[200px] truncate px-4 py-3 text-stone-600">
                        {r.user_email}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[r.status]}`}
                        >
                          {t(`research.statuses.${r.status}`)}
                        </span>
                        {r.error && (
                          <p className="mt-1 max-w-[260px] truncate text-xs text-red-600" title={r.error}>
                            {r.error}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-stone-600">{fmtDate(r.created_at)}</td>
                      <td className="px-4 py-3 text-stone-600">{fmtDate(r.updated_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pager offset={offset} total={data.total} onChange={setOffset} />
        </>
      )}
    </div>
  );
}
